"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { Logo } from "@/components/layout/logo";
import { Button } from "@/components/ui/button";
import { ThemeSwitcher } from "@/components/ui/theme-switcher";
import { MegaMenu } from "@/components/layout/mega-menu";
import { navLinks, services } from "@/lib/data/site";
import { cn } from "@/lib/utils";

export function Navbar() {
  const pathname = usePathname();
  const { resolvedTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [openMega, setOpenMega] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);
  const lightNav = mounted && resolvedTheme === "light";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMega(null);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        setOpenMega(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-50 max-w-[100vw] overflow-x-clip transition-all duration-500",
          scrolled || openMega || mobileOpen
            ? "border-b border-border bg-[var(--nav-bg)] backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
            : "bg-transparent",
        )}
        onMouseLeave={() => setOpenMega(null)}
      >
        <div className="container-wide flex h-14 min-w-0 items-center justify-between gap-2 safe-top sm:h-16 sm:gap-3 md:h-[4.5rem]">
          <Logo className="min-w-0 shrink-0" compact={false} onDark={!lightNav} />

          <nav className="hidden items-center xl:flex" aria-label="Primary">
            {navLinks.map((link) => (
              <div
                key={link.label}
                onMouseEnter={() => setOpenMega(link.mega ?? null)}
                className="relative"
              >
                <Link
                  href={link.href}
                  className={cn(
                    "rounded-full px-2.5 py-2 text-[13px] font-medium transition hover:text-foreground xl:px-3",
                    openMega === link.mega ? "text-foreground" : "text-foreground/85 dark:text-muted",
                  )}
                >
                  {link.label}
                </Link>
              </div>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <ThemeSwitcher onDark={!lightNav} />
            <div className="hidden sm:block">
              <Button href="/contact" size="sm" variant="secondary" cursorLabel="START" className="!h-9 !px-3 text-xs xl:!h-10 xl:!px-4 xl:text-sm">
                <span className="hidden xl:inline">Start a Conversation</span>
                <span className="xl:hidden">Talk to us</span>
              </Button>
            </div>
            <button
              type="button"
              className={cn(
                "inline-flex h-9 w-9 items-center justify-center rounded-full border backdrop-blur transition sm:h-10 sm:w-10 xl:hidden",
                lightNav
                  ? "border-[rgba(11,22,48,0.14)] bg-white/90 text-[#0b1630] hover:border-[rgba(0,96,255,0.35)] hover:bg-white"
                  : "border-white/20 bg-white/10 text-white hover:border-white/35 hover:bg-white/15",
              )}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {openMega ? (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="mega-menu-panel hidden border-t border-border bg-[var(--nav-bg)] backdrop-blur-xl xl:block"
            >
              <MegaMenu type={openMega} />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </header>

      {/* Left sidebar mobile / tablet drawer */}
      <AnimatePresence>
        {mobileOpen ? (
          <>
            <motion.button
              type="button"
              aria-label="Close navigation overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm xl:hidden"
              onClick={closeMobile}
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 34 }}
              className="mobile-nav-drawer fixed inset-y-0 left-0 z-[90] flex w-[min(calc(100vw-2.5rem),22rem)] max-w-[calc(100%-2.5rem)] flex-col border-r shadow-2xl safe-top safe-bottom xl:hidden"
              role="dialog"
              aria-modal="true"
              aria-label="Mobile navigation"
            >
              <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3.5 sm:py-4">
                <Logo className="mobile-nav-logo" onDark={false} />
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={closeMobile}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:py-5">
                <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
                  Menu
                </p>
                <nav className="space-y-1" aria-label="Mobile primary">
                  {navLinks.map((link, i) => (
                    <motion.div
                      key={link.label}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.03 * i }}
                    >
                      <Link
                        href={link.href}
                        onClick={closeMobile}
                        className="flex items-center justify-between rounded-xl px-3 py-3 font-display text-lg tracking-tight text-foreground transition hover:bg-card"
                      >
                        {link.label}
                        <ArrowUpRight className="h-4 w-4 text-muted" />
                      </Link>
                    </motion.div>
                  ))}
                </nav>

                <div className="mt-6 border-t border-border pt-5">
                  <button
                    type="button"
                    onClick={() => setMobileServicesOpen((v) => !v)}
                    className="mb-3 flex w-full items-center justify-between text-[10px] font-semibold uppercase tracking-[0.18em] text-muted"
                  >
                    Services
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 transition",
                        mobileServicesOpen ? "rotate-180" : "",
                      )}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {mobileServicesOpen ? (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="space-y-1.5 overflow-hidden"
                      >
                        {services.map((s) => (
                          <Link
                            key={s.id}
                            href={s.href}
                            onClick={closeMobile}
                            className="block rounded-xl border border-border bg-card/80 px-3 py-2.5 text-sm text-muted transition hover:border-accent/40 hover:text-foreground"
                          >
                            <span className="font-mono text-[10px] text-accent">{s.number}</span>
                            <span className="mt-0.5 block leading-snug">{s.title}</span>
                          </Link>
                        ))}
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              </div>

              <div className="space-y-3 border-t border-border p-4">
                <div className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2">
                  <span className="text-sm text-muted">Theme</span>
                  <ThemeSwitcher onDark={false} />
                </div>
                <Button href="/contact" size="lg" variant="secondary" className="w-full" arrow>
                  Start a Conversation
                </Button>
              </div>
            </motion.aside>
          </>
        ) : null}
      </AnimatePresence>
    </>
  );
}